/* Parent or guardian consent for students aged 13 to 17.
   /guardian-needed  the student's page while they wait (the only account page they can use until consent)
   /guardian         the guardian's page, opened from the emailed link (?token=)
   /guardian/withdraw the guardian's separate link to withdraw consent later (?token=) */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { WEB_ROUTES, asLang, isLang, type GuardianInfo, type Lang } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link, navigate, useLocation } from '../app/nav';
import { useMe, useSession } from '../app/session';
import { useErrorHandler, focusHeading } from '../app/hooks';
import { Alert, Button, Checkbox, Live, Loading, PageHeader, Section, SelectField, TextField } from '../app/ui';
import { api, ApiError, errorAction } from '../lib/api';
import { formatDate } from '../lib/format';
import { looksLikeEmail } from '../lib/forms';
import { readLang } from '../lib/storage';
import { LANG_OPTIONS, translate } from '../lib/i18n';

const POLL_MS = 15000;

/* ---------- the student waiting for consent ---------- */
export function GuardianNeeded() {
  const { t, lang } = useT();
  const me = useMe();
  const { refresh, signOut } = useSession();
  const handle = useErrorHandler();
  const [email, setEmail] = useState('');
  const [gLang, setGLang] = useState<Lang>(lang);
  const [errors, setErrors] = useState<{ email?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [again, setAgain] = useState(false);
  const status = me.consent;

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (status !== 'pending') return;
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, POLL_MS);
    return () => window.clearInterval(id);
  }, [status, refresh]);

  const checkNow = async () => { setChecking(true); await refresh(); setChecking(false); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const value = email.trim();
    let err: string | undefined;
    if (!looksLikeEmail(value)) err = t('v.email');
    else if (value.toLowerCase() === me.user.email.toLowerCase()) err = t('gn.sameEmail');
    setErrors({ email: err });
    if (err) { document.getElementById('gn-email')?.focus(); return; }
    setBusy(true);
    try {
      const r = await api.post<{ status: 'pending'; sent_to: string }>('/api/guardian/request', { guardian_email: value, lang: gLang });
      setSentTo(r.sent_to || value);
      setEmail('');
      setAgain(false);
      await refresh();
    } catch (er) {
      if (er instanceof ApiError && er.code === 'rate_limited') setErrors({ form: t('gn.rateLimited') });
      else { const msg = handle(er, ['guardian_consent_required']); if (msg) setErrors({ form: msg }); }
    } finally { setBusy(false); }
  };

  const showForm = status !== 'granted' && (status !== 'pending' || again);
  const statusBox =
    status === 'granted' ? (
      <div className="status status--ok">
        <p className="status__title">{t('gn.granted.title')}</p>
        <p>{t('gn.granted.text')}</p>
        <div className="actions"><Button onClick={() => navigate(WEB_ROUTES.account)}>{t('gn.granted.go')}</Button></div>
      </div>
    ) : status === 'pending' ? (
      <div className="status">
        <p className="status__title">{t('gn.pending.title')}</p>
        {sentTo && <p>{t('gn.sent', { email: '⁨' + sentTo + '⁩' })}</p>}
        <p>{t('gn.pending.text')}</p>
        <div className="actions">
          <Button variant="secondary" small busy={checking} onClick={checkNow}>{t('gn.check')}</Button>
          {!again && <Button variant="plain" small onClick={() => setAgain(true)}>{t('gn.resend')}</Button>}
        </div>
      </div>
    ) : status === 'denied' || status === 'expired' || status === 'revoked' ? (
      <div className="status status--warn">
        <p className="status__title">{t(`gn.${status}.title`)}</p>
        <p>{t(`gn.${status}.text`)}</p>
      </div>
    ) : null;

  return (
    <div className="narrow-wide">
      <PageHeader title={t('gn.title')} lead={status === 'granted' ? undefined : t('gn.lead')} />
      <Live>{statusBox}</Live>

      {status !== 'granted' && (
        <>
          {showForm && (
            <Section title={t('gn.formTitle')} id="gn-form">
              <form className="card stack" noValidate onSubmit={submit} aria-labelledby="gn-form-title">
                <TextField id="gn-email" label={t('gn.email')} type="email" autoComplete="off" inputMode="email" ltr
                  autoCapitalize="none" spellCheck={false} value={email} onChange={setEmail} error={errors.email} />
                <SelectField id="gn-lang" label={t('gn.lang')} value={gLang} placeholder={false} onChange={v => setGLang(asLang(v))}
                  options={LANG_OPTIONS} />
                {errors.form && <Alert>{errors.form}</Alert>}
                <Button type="submit" block busy={busy}>{t('gn.submit')}</Button>
              </form>
            </Section>
          )}
          <Section title={t('gn.whatTitle')} id="gn-what">
            <ul className="ticks">
              <li>{t('gn.what1')}</li>
              <li>{t('gn.what2')}</li>
              <li>{t('gn.what3')}</li>
            </ul>
          </Section>
          <Section title={t('gn.howTitle')} id="gn-how">
            <ol className="steps">
              <li>{t('gn.how1')}</li>
              <li>{t('gn.how2')}</li>
              <li>{t('gn.how3')}</li>
            </ol>
          </Section>
          <Section title={t('gn.whyTitle')} id="gn-why"><p>{t('gn.why')}</p></Section>
          <div className="section">
            <p className="muted">{t('gn.limited')}</p>
            <p className="linkrow">
              <Link to={WEB_ROUTES.privacy}>{t('footer.privacy')}</Link>
              <Link to={WEB_ROUTES.terms}>{t('footer.terms')}</Link>
              <button type="button" className="linkbtn" onClick={() => void signOut()}>{t('nav.signout')}</button>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- the guardian's page ---------- */
const RELATIONSHIPS = ['mother', 'father', 'guardian'] as const;

export function GuardianConsent() {
  const { t, lang, setLang } = useT();
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  const [info, setInfo] = useState<GuardianInfo | null>(null);
  const [load, setLoad] = useState<'loading' | 'ok' | 'invalid' | 'error'>('loading');
  const [name, setName] = useState('');
  const [rel, setRel] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; rel?: string; confirm?: string; form?: string }>({});
  const [busy, setBusy] = useState<'grant' | 'deny' | null>(null);
  const [result, setResult] = useState<'granted' | 'denied' | null>(null);
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!token) { setLoad('invalid'); return; }
    setLoad('loading');
    api.get<GuardianInfo>(`/api/guardian/consent?token=${encodeURIComponent(token)}`)
      .then(i => {
        setInfo(i);
        setLoad('ok');
        /* the page opens in the language of the email, unless this browser has a chosen language */
        if (!readLang() && isLang(i.lang)) setLang(i.lang, { remember: false });
      })
      .catch(e => setLoad(e instanceof ApiError && (e.code === 'not_found' || e.status === 404) ? 'invalid' : 'error'));
  }, [token, n, setLang]);

  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } if (result) focusHeading(); }, [result]);

  const decide = async (decision: 'grant' | 'deny') => {
    if (busy || !info) return;
    const errs: typeof errors = {};
    if (!name.trim()) errs.name = t('v.required');
    if (!rel) errs.rel = t('v.choose');
    if (!confirm) errs.confirm = t('v.tick');
    setErrors(errs);
    const firstBad = errs.name ? 'gc-name' : errs.rel ? 'gc-rel' : errs.confirm ? 'gc-confirm' : null;
    if (firstBad) { document.getElementById(firstBad)?.focus(); return; }
    setBusy(decision);
    try {
      const r = await api.post<{ status: string }>('/api/guardian/consent',
        { token, decision, guardian_name: name.trim(), relationship: rel, confirm_guardian: true });
      setResult(r.status === 'granted' ? 'granted' : 'denied');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'not_found') { setLoad('invalid'); return; }
      const a = errorAction(e, '', ['signed_out', 'mfa_required', 'guardian_consent_required', 'plan_required']);
      setErrors({ form: a.kind === 'message' ? translate(lang, a.key) : translate(lang, 'err.server_error') });
    } finally { setBusy(null); }
  };

  const student = info ? <bdi dir="ltr">{info.student}</bdi> : null;

  if (load === 'loading') return <div className="narrow"><PageHeader title={t('gc.title')} /><Loading /></div>;
  if (load === 'invalid') {
    return (
      <div className="narrow">
        <PageHeader title={t('gc.invalid.title')} lead={token ? t('gc.invalid.text') : t('gc.missing')} />
      </div>
    );
  }
  if (load === 'error' || !info) {
    return (
      <div className="narrow">
        <PageHeader title={t('gc.title')} />
        <Alert>{t('err.network')} <button type="button" className="linkbtn" onClick={() => setN(x => x + 1)}>{t('common.retry')}</button></Alert>
      </div>
    );
  }
  if (result) {
    return (
      <div className="narrow">
        <PageHeader title={t(result === 'granted' ? 'gc.granted.title' : 'gc.denied.title')} />
        <p className="lead-plain">
          {result === 'granted' ? withStudent(t('gc.granted.text'), student) : t('gc.denied.text')}
        </p>
      </div>
    );
  }
  if (info.status !== 'pending') {
    const key = info.status === 'granted' ? 'gc.already.granted' : info.status === 'denied' ? 'gc.already.denied'
      : info.status === 'revoked' ? 'gc.already.revoked' : 'gc.already.expired';
    return (
      <div className="narrow">
        <PageHeader title={t('gc.title')} />
        <Alert tone="info">{t(key)}</Alert>
      </div>
    );
  }

  return (
    <div className="narrow-wide">
      <PageHeader title={t('gc.title')} lead={withStudent(t('gc.lead'), student)} />
      <Section title={t('gc.aboutTitle')} id="gc-about"><p>{t('gc.about')}</p></Section>
      <Section title={t('gc.storeTitle')} id="gc-store">
        <ul className="ticks">
          <li>{t('gc.store1')}</li>
          <li>{t('gc.store2')}</li>
          <li>{t('gc.store3')}</li>
          <li>{t('gc.store4')}</li>
          <li>{t('gc.store5')}</li>
        </ul>
        <p className="muted mt"><Link to={WEB_ROUTES.privacy}>{t('start.form.privacyLink')}</Link></p>
      </Section>
      <Section title={t('gc.rightsTitle')} id="gc-rights">
        <ul className="ticks">
          <li>{t('gc.rights1', { email: t('company.privacyEmail') })}</li>
          <li>{t('gc.rights2')}</li>
          <li>{t('gc.rights3')}</li>
        </ul>
      </Section>
      <Section title={t('gc.formTitle')} id="gc-form">
        {/* no implicit submit: pressing Enter in a field must never count as a decision */}
        <form className="card stack" noValidate onSubmit={e => e.preventDefault()} aria-labelledby="gc-form-title">
          <TextField id="gc-name" label={t('gc.name')} autoComplete="name" value={name} onChange={setName} error={errors.name} />
          <SelectField id="gc-rel" label={t('gc.relationship')} value={rel} onChange={setRel} error={errors.rel}
            options={RELATIONSHIPS.map(r => ({ value: r, label: t(`rel.${r}`) }))} />
          <Checkbox id="gc-confirm" checked={confirm} onChange={setConfirm} label={t('gc.confirm')} error={errors.confirm} required />
          {errors.form && <Alert>{errors.form}</Alert>}
          <div className="btnrow">
            <Button busy={busy === 'grant'} onClick={() => void decide('grant')}>{t('gc.agree')}</Button>
            <Button variant="gray" busy={busy === 'deny'} onClick={() => void decide('deny')}>{t('gc.decline')}</Button>
          </div>
        </form>
        <p className="section__foot">{t('gc.ignore', { date: formatDate(info.expires_at, lang) })}</p>
      </Section>
    </div>
  );
}

/* puts the masked student email (kept left-to-right) into a sentence */
function withStudent(template: string, student: ReactNode) {
  const [before, after] = template.split('{student}');
  return after === undefined ? template : <>{before}{student}{after}</>;
}

/* ---------- withdraw consent later ---------- */
export function GuardianWithdraw() {
  const { t, lang } = useT();
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<{ confirm?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(!token);
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } focusHeading(); }, [done, invalid]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!confirm) { setError({ confirm: t('v.tick') }); document.getElementById('gw-confirm')?.focus(); return; }
    setBusy(true);
    try {
      await api.post('/api/guardian/withdraw', { token });
      setDone(true);
    } catch (er) {
      if (er instanceof ApiError && (er.code === 'not_found' || er.status === 404)) { setInvalid(true); return; }
      const a = errorAction(er, '', ['signed_out', 'mfa_required', 'guardian_consent_required', 'plan_required']);
      setError({ form: a.kind === 'message' ? translate(lang, a.key) : translate(lang, 'err.server_error') });
    } finally { setBusy(false); }
  };

  if (invalid) return <div className="narrow"><PageHeader title={t('gc.invalid.title')} lead={t('gw.invalid')} /></div>;
  if (done) return <div className="narrow"><PageHeader title={t('gw.done.title')} lead={t('gw.done.text')} /></div>;
  return (
    <div className="narrow">
      <PageHeader title={t('gw.title')} lead={t('gw.lead')} />
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="page-title">
        <p className="muted">{t('gw.kept')}</p>
        <Checkbox id="gw-confirm" checked={confirm} onChange={setConfirm} label={t('gw.confirm')} error={error.confirm} required />
        {error.form && <Alert>{error.form}</Alert>}
        <Button type="submit" variant="danger-solid" block busy={busy}>{t('gw.submit')}</Button>
      </form>
    </div>
  );
}
